import React, { useMemo } from 'react';
import { Table } from 'antd';
import moment from 'moment';
import {
	WrenchModeEvent,
	formatDuration,
	countCompletedWorkOrders,
	parseLocationEvents,
	collectScheduleEvents,
	filterEventsByLocation,
} from '../../utils/WrenchModeEventUtils';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';

interface FlattenedRow {
	key: string;
	scheduleId: number;
	date: string;
	locationName: string;
	locationId: number;
	workOrdersCompleted: number;
	workOrdersTotal: number;
	driveTimeMs: number | null;
	workTimeMs: number | null;
}

interface WrenchModeSchedulesTableProps {
	schedules: any[];
	loading: boolean;
	highlightedDate: string | null;
	onRowClick: (date: string, locationId: number) => void;
	locationIds: number[] | undefined;
}

const WrenchModeSchedulesTable: React.FC<WrenchModeSchedulesTableProps> = ({
	schedules,
	loading,
	highlightedDate,
	onRowClick,
	locationIds,
}) => {
	const hasLocationFilter = locationIds && locationIds.length > 0;

	const { rows, rowSpanMap } = useMemo(() => {
		const flatRows: FlattenedRow[] = [];

		for (const schedule of schedules) {
			const locations = schedule.locations || [];

			// Skip schedules with no events at all
			const allEvents = collectScheduleEvents(schedule);
			if (allEvents.length === 0) continue;

			if (locations.length === 0) {
				if (!hasLocationFilter) {
					flatRows.push({
						key: schedule.id + '-0',
						scheduleId: schedule.id,
						date: schedule.date,
						locationName: 'No location',
						locationId: 0,
						workOrdersCompleted: 0,
						workOrdersTotal: 0,
						driveTimeMs: null,
						workTimeMs: null,
					});
				}
			} else {
				for (let i = 0; i < locations.length; i++) {
					const loc = locations[i];

					if (hasLocationFilter && locationIds.indexOf(loc.locationId) === -1) continue;

					const locationName = (loc.location && loc.location.name) || 'Unknown Location';
					const workOrderIds = loc.workOrderIds || [];

					// Parse per-location events for accurate drive/work time
					const locEvents = loc.events || [];
					let driveTimeMs: number | null = null;
					let workTimeMs: number | null = null;

					if (locEvents.length > 0) {
						const locParsed = parseLocationEvents(locEvents);
						driveTimeMs = locParsed.driveTimeMs;
						workTimeMs = locParsed.totalWorkMs;
					}

					flatRows.push({
						key: schedule.id + '-' + i,
						scheduleId: schedule.id,
						date: schedule.date,
						locationName,
						locationId: loc.locationId,
						workOrdersCompleted: countCompletedWorkOrders(
							workOrderSearchAndAPIAdaptor(
								((loc && loc.workOrders) || []).map((wo) => ({ source: wo }))
							),
							schedule.supplierEmail,
							schedule.date
						),
						workOrdersTotal: workOrderIds.length,
						driveTimeMs,
						workTimeMs,
					});
				}
			}
		}

		// Compute rowSpan for date column
		const spanMap: Record<string, number> = {};

		for (let i = 0; i < flatRows.length; ) {
			const currentDate = flatRows[i].date;
			let groupEnd = i + 1;

			while (groupEnd < flatRows.length && flatRows[groupEnd].date === currentDate) {
				groupEnd++;
			}

			spanMap[flatRows[i].key] = groupEnd - i;

			for (let j = i + 1; j < groupEnd; j++) {
				spanMap[flatRows[j].key] = 0;
			}

			i = groupEnd;
		}

		return {
			rows: flatRows,
			rowSpanMap: spanMap,
		};
	}, [schedules, hasLocationFilter, locationIds]);

	const columns = [
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			render: (date: string, record: FlattenedRow) => ({
				children: moment(date).format('ddd, MMM D'),
				props: { rowSpan: rowSpanMap[record.key] },
			}),
		},
		{
			title: 'Location',
			dataIndex: 'locationName',
			key: 'locationName',
		},
		{
			title: 'Work Orders',
			key: 'workOrders',
			render: (_: any, record: FlattenedRow) =>
				record.workOrdersCompleted + '/' + record.workOrdersTotal,
		},
		{
			title: 'Drive Time',
			key: 'driveTime',
			render: (_: any, record: FlattenedRow) => {
				if (record.driveTimeMs === null) return '-';
				return formatDuration(record.driveTimeMs);
			},
		},
		{
			title: 'Work Time',
			key: 'workTime',
			render: (_: any, record: FlattenedRow) => {
				if (record.workTimeMs === null) return '-';
				return formatDuration(record.workTimeMs);
			},
		},
	];

	return (
		<Table
			className="wrenchModeTable"
			columns={columns}
			dataSource={rows}
			pagination={false}
			loading={loading}
			rowClassName={(record: FlattenedRow) => {
				if (highlightedDate && record.date === highlightedDate) {
					return 'wrench-mode-row-highlighted';
				}
				return '';
			}}
			onRow={(record: FlattenedRow) => ({
				onClick: () => onRowClick(record.date, record.locationId),
				style: { cursor: 'pointer' },
			})}
			size="middle"
			locale={{ emptyText: loading ? ' ' : 'No logs for this month' }}
		/>
	);
};

export default WrenchModeSchedulesTable;
